import EXIF from 'exif-js';

class CameraCapture {
  constructor() {
    this.stream = null;
    this.constraints = {
      video: {
        width: { ideal: 1280 },
        height: { ideal: 720 },
        facingMode: 'user' // Front camera by default
      },
      audio: false
    };
    this.metadata = {};
    this.resilienceCleanup = null;
  }

  // Initialize camera stream with retry logic and extra safety checks
  async initializeCamera(facingMode = 'user', retryCount = 0) {
    const maxRetries = 2;

    try {
      console.log(`CameraCapture: Initializing camera (attempt ${retryCount + 1}/${maxRetries + 1}) with facingMode: ${facingMode}`);
      
      // EXTRA CHECK 1: Ensure HTTPS context (except localhost)
      if (!window.isSecureContext && !window.location.hostname.includes('localhost')) {
        throw new Error('Camera access requires HTTPS. Please use a secure connection.');
      }
      
      // EXTRA CHECK 2: Stop any existing streams first (only one stream at a time)
      await this.stopAllExistingStreams();
      
      // Build constraints
      const deviceId = await this.getPreferredDeviceId(facingMode);
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

      const baseVideo = deviceId
        ? { deviceId: { exact: deviceId } }
        : { facingMode: { ideal: facingMode } };

      const video = isMobile
        ? { ...baseVideo, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30, max: 30 } }
        : { ...baseVideo, width: { ideal: 1280 }, height: { ideal: 720 } };

      this.constraints = { video, audio: false };

      // Request stream with fallbacks
      this.stream = await this.getStreamWithFallbacks(this.constraints);

      console.log('CameraCapture: Stream obtained successfully', {
        active: this.stream.active,
        tracks: this.stream.getVideoTracks().length
      });

      return {
        success: true,
        stream: this.stream,
        message: 'Camera initialized successfully',
        facingMode,
        attempt: retryCount + 1
      };
    } catch (error) {
      console.error(`Camera init error (attempt ${retryCount + 1}):`, error);

      // Your existing retry logic unchanged
      if (retryCount < maxRetries && facingMode === 'user') {
        await new Promise(r => setTimeout(r, 500));
        return this.initializeCamera('environment', retryCount + 1);
      }
      if (retryCount < maxRetries && facingMode === 'environment') {
        await new Promise(r => setTimeout(r, 500));
        return this.initializeCamera('user', retryCount + 1);
      }

      let errorMessage = 'Failed to access camera';
      if (error.name === 'NotAllowedError') errorMessage = 'Camera permission denied. Please allow camera access and try again.';
      else if (error.name === 'NotFoundError') errorMessage = 'No camera found on this device.';
      else if (error.name === 'NotSupportedError') errorMessage = 'Camera not supported on this device.';
      else if (error.name === 'OverconstrainedError') errorMessage = 'Camera constraints not supported.';
      else if (error.message && error.message.includes('HTTPS')) errorMessage = error.message;

      return { success: false, error: errorMessage, originalError: error.name };
    }
  }

  // Wait for video element to be ready
  async waitForVideoReady(videoElement, timeout = 10000) {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(new Error('Video ready timeout'));
      }, timeout);

      const checkReady = () => {
        if (videoElement.readyState >= 2 && videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
          clearTimeout(timeoutId);
          console.log('CameraCapture: Video is ready', {
            readyState: videoElement.readyState,
            videoWidth: videoElement.videoWidth,
            videoHeight: videoElement.videoHeight
          });
          resolve(true);
        } else {
          // Continue checking
          setTimeout(checkReady, 100);
        }
      };

      // Start checking immediately
      checkReady();
    });
  }

  // Capture photo from video stream with improved readiness check
  async capturePhoto(videoElement, mirrorMode = false) {
    try {
      console.log('CameraCapture: Starting photo capture...', { mirrorMode });
      
      if (!videoElement || !this.stream) {
        throw new Error('Camera not initialized or video element not provided');
      }

      console.log('CameraCapture: Initial video element state:', {
        videoWidth: videoElement.videoWidth,
        videoHeight: videoElement.videoHeight,
        readyState: videoElement.readyState
      });

      // Wait for video to be ready with timeout
      try {
        await this.waitForVideoReady(videoElement, 5000);
      } catch (error) {
        throw new Error('Video not ready for capture. Please wait for camera to load completely.');
      }

      // Create canvas to capture frame
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      // Set canvas dimensions to match video
      canvas.width = videoElement.videoWidth || 640;
      canvas.height = videoElement.videoHeight || 480;
      
      console.log('CameraCapture: Canvas dimensions set:', {
        width: canvas.width,
        height: canvas.height
      });
      
      // DO NOT apply mirroring during canvas capture
      // Let CSS handle all mirroring for consistent display
      console.log('CameraCapture: Capturing without canvas mirroring for consistent display');
      
      // Draw current video frame to canvas (without mirroring)
      context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
      console.log('CameraCapture: Frame drawn to canvas');
      
      // Convert canvas to data URL first (synchronous)
      const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
      console.log('CameraCapture: Data URL generated, length:', dataUrl.length);
      
      // Convert canvas to blob with timeout
      const blob = await Promise.race([
        new Promise(resolve => {
          canvas.toBlob(resolve, 'image/jpeg', 0.8);
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Blob conversion timeout')), 5000)
        )
      ]);
      
      console.log('CameraCapture: Blob created, size:', blob.size);

      // Generate metadata with timeout
      console.log('CameraCapture: Generating metadata...');
      const metadata = await Promise.race([
        this.generatePhotoMetadata(blob, mirrorMode),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Metadata generation timeout')), 3000)
        )
      ]);
      
      console.log('CameraCapture: Metadata generated successfully');
      
      return {
        success: true,
        blob: blob,
        dataUrl: dataUrl,
        metadata: metadata
      };
    } catch (error) {
      console.error('Photo capture error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Generate photo metadata for validation
  async generatePhotoMetadata(blob, mirrorMode = false) {
    const metadata = {
      captureTime: new Date(),
      timestamp: Date.now(),
      fileSize: blob.size,
      mimeType: blob.type,
      isLiveCapture: true,
      mirrorMode: mirrorMode,
      deviceInfo: this.getDeviceInfo(),
      cameraConstraints: this.constraints,
      streamActive: this.stream && this.stream.active,
      validation: {
        isRecent: true,
        hasValidStream: this.stream && this.stream.active,
        properFileSize: blob.size > 10000 && blob.size < 5000000, // 10KB - 5MB
        validMimeType: blob.type === 'image/jpeg'
      }
    };

    // Try to extract EXIF data
    try {
      const exifData = await this.extractExifData(blob);
      metadata.exif = exifData;
      metadata.validation.hasExifData = Object.keys(exifData).length > 0;
    } catch (error) {
      console.warn('Could not extract EXIF data:', error);
      metadata.validation.hasExifData = false;
    }

    return metadata;
  }

  // Extract EXIF data from image blob
  extractExifData(blob) {
    return new Promise((resolve) => {
      // Set a timeout for EXIF extraction
      const timeout = setTimeout(() => {
        console.warn('EXIF extraction timed out');
        resolve({});
      }, 2000);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const arrayBuffer = e.target.result;
          
          // Use EXIF.js to extract metadata with timeout protection
          EXIF.readFromBinaryFile(arrayBuffer, (exifData) => {
            clearTimeout(timeout);
            resolve(exifData || {});
          });
        } catch (error) {
          clearTimeout(timeout);
          console.warn('EXIF extraction failed:', error);
          resolve({});
        }
      };
      
      reader.onerror = () => {
        clearTimeout(timeout);
        console.warn('FileReader error during EXIF extraction');
        resolve({});
      };
      
      reader.readAsArrayBuffer(blob);
    });
  }

  // Get device information
  getDeviceInfo() {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      colorDepth: window.screen.colorDepth,
      pixelDepth: window.screen.pixelDepth,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timestamp: Date.now()
    };
  }

  // Validate live capture (anti-cheat measures)
  validateLiveCapture(metadata) {
    const validation = {
      isValid: true,
      issues: [],
      score: 100
    };

    // Check if capture is recent (within last 30 seconds)
    const timeDiff = Date.now() - metadata.timestamp;
    if (timeDiff > 30000) {
      validation.issues.push('Photo not captured recently');
      validation.score -= 30;
    }

    // Check if stream was active during capture
    if (!metadata.validation.hasValidStream) {
      validation.issues.push('Camera stream not active during capture');
      validation.score -= 40;
    }

    // Check file size (too small might be fake, too large might be uploaded)
    if (!metadata.validation.properFileSize) {
      validation.issues.push('Suspicious file size');
      validation.score -= 20;
    }

    // Check MIME type
    if (!metadata.validation.validMimeType) {
      validation.issues.push('Invalid file format');
      validation.score -= 20;
    }

    // Check device consistency
    if (!metadata.deviceInfo.onLine) {
      validation.issues.push('Device appears to be offline');
      validation.score -= 10;
    }

    validation.isValid = validation.score >= 70;
    return validation;
  }

  // Nuclear restart - complete camera reinitialization
  async performNuclearRestart(facingMode = null) {
    try {
      console.log('CameraCapture: Performing nuclear restart...');
      
      // 1. Complete stream shutdown
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }
      
      // 2. Reset all internal state
      this.metadata = {};
      
      // 3. Use current facing mode if none specified
      const targetFacingMode = facingMode || this.constraints.video.facingMode;
      
      // 4. Force fresh initialization with delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // 5. Fresh initialization
      const result = await this.initializeCamera(targetFacingMode, 0);
      
      if (result.success) {
        console.log('CameraCapture: Nuclear restart successful');
      } else {
        console.log('CameraCapture: Nuclear restart failed, trying alternate camera');
        // Try the other camera as fallback
        const alternateFacingMode = targetFacingMode === 'user' ? 'environment' : 'user';
        return await this.initializeCamera(alternateFacingMode, 0);
      }
      
      return result;
    } catch (error) {
      console.error('Nuclear restart error:', error);
      return {
        success: false,
        error: 'Nuclear restart failed'
      };
    }
  }

  // Progressive nuclear restart with multiple strategies
  async performProgressiveNuclearRestart() {
    console.log('CameraCapture: Starting progressive nuclear restart...');
    
    const strategies = [
      { facingMode: 'user', description: 'Front camera nuclear restart' },
      { facingMode: 'environment', description: 'Back camera nuclear restart' },
      { facingMode: 'user', description: 'Front camera retry after delay' },
      { facingMode: 'environment', description: 'Back camera retry after delay' }
    ];
    
    for (let i = 0; i < strategies.length; i++) {
      const strategy = strategies[i];
      console.log(`CameraCapture: Trying strategy ${i + 1}/${strategies.length}: ${strategy.description}`);
      
      const result = await this.performNuclearRestart(strategy.facingMode);
      
      if (result.success) {
        console.log(`CameraCapture: Strategy ${i + 1} succeeded`);
        return result;
      }
      
      // Delay between attempts (longer delay for retries)
      const delay = i < 2 ? 1000 : 2000;
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    
    return {
      success: false,
      error: 'All nuclear restart strategies failed'
    };
  }

  // Switch camera (front/back) - now uses nuclear restart
  async switchCamera() {
    try {
      const currentFacingMode = this.constraints.video.facingMode;
      const newFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';
      
      // Use nuclear restart for camera switching
      return await this.performNuclearRestart(newFacingMode);
    } catch (error) {
      console.error('Camera switch error:', error);
      return {
        success: false,
        error: 'Failed to switch camera'
      };
    }
  }

  // Stop camera stream
  stopCamera() {
    try {
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }
      return { success: true };
    } catch (error) {
      console.error('Stop camera error:', error);
      return {
        success: false,
        error: 'Failed to stop camera'
      };
    }
  }

  // Check camera availability
  static async checkCameraAvailability() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      
      return {
        available: videoDevices.length > 0,
        deviceCount: videoDevices.length,
        devices: videoDevices.map(device => ({
          deviceId: device.deviceId,
          label: device.label || `Camera ${videoDevices.indexOf(device) + 1}`,
          groupId: device.groupId
        }))
      };
    } catch (error) {
      console.error('Camera availability check error:', error);
      return {
        available: false,
        deviceCount: 0,
        devices: [],
        error: error.message
      };
    }
  }

  // Check browser support
  static checkBrowserSupport() {
    const support = {
      getUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
      enumerateDevices: !!(navigator.mediaDevices && navigator.mediaDevices.enumerateDevices),
      canvas: !!document.createElement('canvas').getContext,
      blob: !!window.Blob,
      fileReader: !!window.FileReader
    };

    support.isSupported = Object.values(support).every(Boolean);
    return support;
  }

  // Analyze video content to detect black screen
  static analyzeVideoContent(videoElement) {
    try {
      if (!videoElement || videoElement.readyState < 2) {
        return {
          isBlackScreen: true,
          confidence: 0,
          reason: 'Video not ready'
        };
      }

      // Create canvas to analyze video frame
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      
      canvas.width = videoElement.videoWidth || 320;
      canvas.height = videoElement.videoHeight || 240;
      
      // Draw current video frame
      context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
      
      // Get image data for analysis
      const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      
      // Analyze pixel data
      let totalBrightness = 0;
      let nonZeroPixels = 0;
      let colorVariance = 0;
      
      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        
        const brightness = (r + g + b) / 3;
        totalBrightness += brightness;
        
        if (brightness > 0) {
          nonZeroPixels++;
        }
        
        // Calculate color variance
        const avgColor = brightness;
        colorVariance += Math.abs(r - avgColor) + Math.abs(g - avgColor) + Math.abs(b - avgColor);
      }
      
      const totalPixels = data.length / 4;
      const avgBrightness = totalBrightness / totalPixels;
      const nonZeroRatio = nonZeroPixels / totalPixels;
      const avgColorVariance = colorVariance / totalPixels;
      
      // Determine if it's a black screen
      const isBlackScreen = avgBrightness < 10 && nonZeroRatio < 0.1 && avgColorVariance < 5;
      const confidence = isBlackScreen ? 
        Math.min(100, (10 - avgBrightness) * 10 + (0.1 - nonZeroRatio) * 500) : 
        Math.min(100, avgBrightness + nonZeroRatio * 100);
      
      return {
        isBlackScreen,
        confidence: Math.round(confidence),
        avgBrightness: Math.round(avgBrightness),
        nonZeroRatio: Math.round(nonZeroRatio * 100) / 100,
        avgColorVariance: Math.round(avgColorVariance),
        reason: isBlackScreen ? 'Low brightness and color variance detected' : 'Normal video content detected'
      };
    } catch (error) {
      console.warn('Video analysis failed:', error);
      return {
        isBlackScreen: true,
        confidence: 0,
        reason: 'Analysis failed: ' + error.message
      };
    }
  }

  // Check if video element shows black screen
  static isVideoBlackScreen(videoElement, options = {}) {
    const {
      confidenceThreshold = 70,
      requireMultipleSamples = true,
      sampleCount = 3,
      sampleInterval = 500
    } = options;

    return new Promise((resolve) => {
      if (!requireMultipleSamples) {
        const analysis = CameraCapture.analyzeVideoContent(videoElement);
        resolve({
          isBlackScreen: analysis.isBlackScreen && analysis.confidence >= confidenceThreshold,
          analysis,
          samples: [analysis]
        });
        return;
      }

      const samples = [];
      let currentSample = 0;

      const takeSample = () => {
        const analysis = CameraCapture.analyzeVideoContent(videoElement);
        samples.push(analysis);
        currentSample++;

        if (currentSample >= sampleCount) {
          // Analyze all samples
          const blackScreenSamples = samples.filter(s => s.isBlackScreen && s.confidence >= confidenceThreshold);
          const isBlackScreen = blackScreenSamples.length >= Math.ceil(sampleCount * 0.6); // 60% threshold
          
          resolve({
            isBlackScreen,
            analysis: samples[samples.length - 1], // Latest sample
            samples,
            blackScreenRatio: blackScreenSamples.length / sampleCount
          });
        } else {
          setTimeout(takeSample, sampleInterval);
        }
      };

      takeSample();
    });
  }

  // EXTRA CHECK 3: Stop all existing streams (only one stream at a time)
  async stopAllExistingStreams() {
    try {
      // Stop our own stream first
      if (this.stream) {
        this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      }
      
      // Try to find and stop any other video elements with streams
      const videoElements = document.querySelectorAll('video');
      videoElements.forEach(video => {
        if (video.srcObject) {
          const stream = video.srcObject;
          if (stream && stream.getTracks) {
            stream.getTracks().forEach(track => track.stop());
          }
          video.srcObject = null;
        }
      });
      
      console.log('CameraCapture: All existing streams stopped');
    } catch (error) {
      console.warn('CameraCapture: Error stopping existing streams:', error);
    }
  }

  // EXTRA CHECK 4: Ensure video container is visible before starting
  ensureVideoContainerVisible(videoElement) {
    const container = videoElement.closest('div, section, main');
    if (container) {
      const style = window.getComputedStyle(container);
      if (style.display === 'none' || style.visibility === 'hidden') {
        console.warn('CameraCapture: Video container is hidden - this may cause videoWidth to remain 0');
        return false;
      }
    }
    return true;
  }

  // Attach stream to a video element (iOS-safe with extra checks)
  async startPreview(videoElement) {
    console.log('CameraCapture: Starting preview...', {
      hasStream: !!this.stream,
      streamActive: this.stream?.active,
      videoElement: !!videoElement
    });

    if (!this.stream) {
      console.error('CameraCapture: No stream available for preview');
      throw new Error('No stream to preview - camera initialization may have failed');
    }

    if (!this.stream.active) {
      console.error('CameraCapture: Stream is not active');
      throw new Error('Camera stream is not active - please reinitialize camera');
    }

    if (!videoElement) {
      console.error('CameraCapture: No video element provided');
      throw new Error('No video element provided for preview');
    }

    // EXTRA CHECK 4: Don't start camera in hidden container
    if (!this.ensureVideoContainerVisible(videoElement)) {
      console.warn('CameraCapture: Starting preview in potentially hidden container');
    }

    try {
      // iOS/Safari autoplay requirements - ESSENTIAL for iOS PWA/in-app browsers
      videoElement.setAttribute('playsinline', ''); // Critical for iOS
      videoElement.muted = true; // Essential - allow autoplay without user gesture
      videoElement.autoplay = true; // Required for automatic playback

      // Clear any existing srcObject first
      if (videoElement.srcObject) {
        console.log('CameraCapture: Clearing existing srcObject');
        videoElement.srcObject = null;
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      console.log('CameraCapture: Setting srcObject to stream');
      videoElement.srcObject = this.stream;

      // Wait for metadata to load with timeout
      console.log('CameraCapture: Waiting for metadata to load...');
      await Promise.race([
        new Promise(resolve => {
          if (videoElement.readyState >= 1) {
            console.log('CameraCapture: Metadata already loaded');
            return resolve();
          }
          const onLoaded = () => {
            console.log('CameraCapture: Metadata loaded event fired');
            videoElement.removeEventListener('loadedmetadata', onLoaded);
            resolve();
          };
          videoElement.addEventListener('loadedmetadata', onLoaded);
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Metadata loading timeout')), 10000)
        )
      ]);

      // iOS PWA / in-app browser quirks: The muted + playsInline + video.play() combo is essential
      console.log('CameraCapture: Attempting to play video...');
      try {
        await videoElement.play();
        console.log('CameraCapture: iOS-safe video.play() succeeded');
      } catch (err) {
        console.warn('CameraCapture: video.play() failed (usually harmless on some browsers):', err);
        // On some browsers, this is expected and harmless
      }

      // Additional check: Ensure video dimensions are available after a brief delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (videoElement.videoWidth === 0 || videoElement.videoHeight === 0) {
        console.warn('CameraCapture: Video dimensions still 0 after preview start - container may be hidden');
        throw new Error('Video preview failed - no video dimensions available');
      } else {
        console.log(`CameraCapture: Video preview ready - ${videoElement.videoWidth}x${videoElement.videoHeight}`);
      }

    } catch (error) {
      console.error('CameraCapture: Preview start failed:', error);
      throw new Error(`Failed to start video preview: ${error.message}`);
    }
  }

  // Pick a specific deviceId (more reliable than facingMode on many phones)
  async getPreferredDeviceId(facingMode = 'user') {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cams = devices.filter(d => d.kind === 'videoinput');
      if (!cams.length) return null;

      const wantBack = facingMode === 'environment';
      // Try to match by label
      const match = cams.find(d => {
        const label = (d.label || '').toLowerCase();
        return wantBack
          ? label.includes('back') || label.includes('rear') || label.includes('environment')
          : label.includes('front') || label.includes('user');
      });

      return (match || (wantBack ? cams[cams.length - 1] : cams[0])).deviceId || null;
    } catch {
      return null;
    }
  }

  // Safer getUserMedia with layered fallbacks
  async getStreamWithFallbacks(baseConstraints) {
    try {
      return await navigator.mediaDevices.getUserMedia(baseConstraints);
    } catch (e) {
      console.warn('Primary getUserMedia failed:', e);
      // Try simpler (no width/height/framerate)
      if (e.name === 'OverconstrainedError' || e.name === 'NotReadableError') {
        try {
          return await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        } catch (e2) {
          console.warn('Fallback getUserMedia failed:', e2);
          throw e2;
        }
      }
      throw e;
    }
  }

  // After preview starts, verify we aren't black; auto-recover if needed
  async ensureNotBlack(videoElement) {
    const res = await CameraCapture.isVideoBlackScreen(videoElement, {
      sampleCount: 4,
      sampleInterval: 350,
      confidenceThreshold: 60
    });

    if (res.isBlackScreen) {
      console.warn('Black screen detected; attempting progressive restart...');
      const restart = await this.performProgressiveNuclearRestart();
      if (restart.success) {
        await this.startPreview(videoElement);
      } else {
        throw new Error('Camera black screen and restart failed');
      }
    }
  }

  // Re-init when tab becomes visible or tracks end
  attachResilience(videoElement) {
    const onVisibility = async () => {
      if (document.visibilityState === 'visible') {
        const tracks = (this.stream?.getVideoTracks?.() || []);
        const ended = tracks.some(t => t.readyState === 'ended');
        if (!this.stream || ended) {
          await this.performProgressiveNuclearRestart(this.constraints?.video?.facingMode || 'user');
          await this.startPreview(videoElement);
        }
      }
    };

    const onEnded = async () => {
      await this.performProgressiveNuclearRestart(this.constraints?.video?.facingMode || 'user');
      await this.startPreview(videoElement);
    };

    document.addEventListener('visibilitychange', onVisibility);
    (this.stream?.getVideoTracks?.() || []).forEach(t => t.addEventListener('ended', onEnded));

    // Store cleanup function
    this.resilienceCleanup = () => {
      document.removeEventListener('visibilitychange', onVisibility);
      (this.stream?.getVideoTracks?.() || []).forEach(t => t.removeEventListener('ended', onEnded));
    };

    // Return cleanup
    return this.resilienceCleanup;
  }
}

export default CameraCapture;
export { CameraCapture };
